/**
 * TimesheetEntryTab — таб «Заполнить время» раздела «Учёт времени» (задача 85 §5.3).
 *
 * Развязка с табом «Подтверждение»: здесь выбор сотрудника и **любой** даты
 * (свободный, без привязки к недельной полосе подтверждения) → справа тот же
 * док-редактор (`EntryDock` → `TimesheetDayEditor`), что и при клике по плашке
 * дня в консоли. Второго редактора нет — переиспускаем существующие примитивы
 * (`patterns.md §Рабочее место руководителя`, §Единый редактор записи времени,
 * §Combobox).
 *
 * Список сотрудников — `useAdminTimesheetWorkers` (scope резолвит backend
 * `/admin/timesheet/users`: admin → все, лид → своё поддерево). Эндпоинт
 * отдаёт timesheet-субъектов поддерева — и `worker`, и `office_staff` (задача
 * 108 §1.1); фронт роли не различает, рендерит список как есть. «Себя»
 * добавляем отдельной первой опцией из сессии — backend пропускает self-ветку
 * без нового эндпоинта.
 *
 * `weekStart` выводим из выбранной даты тем же `isoMonday(parseISO(date))`, что
 * роут применяет к консоли; `unitId` — `null` (backend резолвит scope сам по
 * `user_id`, см. архитектурный assessment §5.3).
 */
import * as React from "react";
import { useTranslation } from "react-i18next";
import { addDays, format, parseISO } from "date-fns";

import { Surface } from "@/components/ui/surface";
import { DateField } from "@/components/ui/date-field";
import { Combobox, type ComboboxOption } from "@/components/ui/combobox";
import { EntryDock, type EntryDockSubject } from "@/components/approval/EntryDock";
import { useAdminTimesheet, useAdminTimesheetWorkers } from "@/hooks/useAdminTimesheet";
import { useSession } from "@/store/session";

interface TimesheetEntryTabProps {
  /** Выбранный сотрудник (`?entry_user=`); null = ещё не выбран. */
  selectedUserId: number | null;
  /** Выбранная дата (`?entry_date=`, ISO); null = ещё не выбрана. */
  selectedDate: string | null;
  onUserChange: (userId: number) => void;
  onDateChange: (date: string) => void;
  /**
   * 73 §2.3 — селектор других людей. `true` (по умолчанию, `/time-approval`):
   * комбобокс «я + подчинённые». `false` (офис «Ввод времени» без подчинённых,
   * напр. pmo): субъект зафиксирован на себе, вместо селектора — строка
   * идентичности, `/admin/timesheet/users` не дёргаем (там 403 для офиса).
   */
  showSubordinatePicker?: boolean;
  /**
   * 73 §2.3 — при пустом выборе подставить себя + сегодня (офис «Ввод времени»
   * открывается сразу на своём дне). По умолчанию `false` — `/time-approval`
   * показывает пустое состояние, пока сотрудник/дата не выбраны явно.
   */
  defaultToSelf?: boolean;
}

const ISO_RE = /^\d{4}-\d{2}-\d{2}$/;

function isoMonday(d: Date): string {
  const dow = (d.getDay() + 6) % 7; // 0 = Monday
  return format(addDays(d, -dow), "yyyy-MM-dd");
}

export function TimesheetEntryTab({
  selectedUserId,
  selectedDate,
  onUserChange,
  onDateChange,
  showSubordinatePicker = true,
  defaultToSelf = false,
}: TimesheetEntryTabProps): React.ReactElement {
  const { t } = useTranslation();
  const currentUserId = useSession((s) => s.user?.id ?? null);
  const currentUserName = useSession((s) => s.user?.display_name ?? "");
  // Список подчинённых нужен только когда селектор виден: у офиса без подчинённых
  // `/admin/timesheet/users` отдаёт 403 (§1.3) — не дёргаем ручку впустую.
  const workersQuery = useAdminTimesheetWorkers(showSubordinatePicker);

  // Дефолт «я + сегодня» для офис-точки входа: пустой выбор эквивалентен себе,
  // чтобы редактор открылся на своём дне без лишней навигации (73 §2.3).
  const today = format(new Date(), "yyyy-MM-dd");
  const effectiveUserId = showSubordinatePicker
    ? (selectedUserId ?? (defaultToSelf ? currentUserId : null))
    : currentUserId;
  const effectiveDate = selectedDate ?? (defaultToSelf ? today : null);
  const isSelf = effectiveUserId !== null && effectiveUserId === currentUserId;

  // «Я (руководитель)» — отдельная первая опция из сессии: backend принимает
  // self по `user_id == actor.id`. Эндпоинт (задача 108 §1.1) отдаёт субъектов
  // поддерева, включая `office_staff`, — лид-office_staff может попасть в
  // выборку, поэтому дедупим его в цикле ниже (`w.id === currentUserId`). У
  // admin без своей карточки-субъекта опция всё равно валидна (backend
  // отработает по self-ветке). Прячем, если сессия ещё не подъехала (нет id).
  const options: ComboboxOption[] = React.useMemo(() => {
    const opts: ComboboxOption[] = [];
    if (currentUserId !== null) {
      opts.push({
        value: String(currentUserId),
        label: currentUserName,
        hint: t("timeApproval.entryTab.selfHint"),
      });
    }
    for (const w of workersQuery.data ?? []) {
      if (w.id === currentUserId) continue; // self уже добавлен отдельной опцией
      opts.push({
        value: String(w.id),
        label: w.display_name,
        hint: w.position ?? w.unit_name ?? undefined,
      });
    }
    return opts;
  }, [workersQuery.data, currentUserId, currentUserName, t]);

  const selectedName = isSelf
    ? currentUserName
    : (workersQuery.data?.find((w) => w.id === effectiveUserId)?.display_name ?? "");
  const selectedPosition = isSelf
    ? null
    : (workersQuery.data?.find((w) => w.id === effectiveUserId)?.position ?? null);

  const hasValidSelection =
    effectiveUserId !== null && effectiveDate !== null && ISO_RE.test(effectiveDate);

  // Док показываем по факту валидного выбора; закрытие (X / клик по оверлею)
  // прячет его до смены сотрудника/даты — тогда `dismissedKey` сбрасывается.
  const selectionKey = hasValidSelection ? `${effectiveUserId}:${effectiveDate}` : null;
  const [dismissedKey, setDismissedKey] = React.useState<string | null>(null);
  const dockOpen = hasValidSelection && dismissedKey !== selectionKey;

  // Режим (правка/внесение) дока выводим из снапшота выбранного дня: есть записи
  // → правка, иначе внесение за несдавшего. Тот же `EntryDock` → `TimesheetDayEditor`.
  const snapshot = useAdminTimesheet(
    hasValidSelection ? effectiveUserId : null,
    hasValidSelection ? effectiveDate : null,
  );
  const isEdit = (snapshot.data?.entries.length ?? 0) > 0;

  const subject: EntryDockSubject | null =
    dockOpen && effectiveUserId !== null
      ? { userId: effectiveUserId, name: selectedName, position: selectedPosition, isEdit, isSelf }
      : null;

  return (
    <div className="flex flex-col gap-4">
      <Surface className="flex flex-col gap-3 p-3 sm:flex-row sm:items-end">
        <div className="flex flex-1 flex-col gap-1.5">
          <span className="font-condensed text-[10px] uppercase tracking-[0.22em] text-[var(--fg-3)]">
            {showSubordinatePicker
              ? t("timeApproval.workerPicker.label")
              : t("timeApproval.entryTab.selfSubjectLabel")}
          </span>
          {showSubordinatePicker ? (
            <Combobox
              options={options}
              value={effectiveUserId === null ? null : String(effectiveUserId)}
              onChange={(value) => {
                if (value === null) return;
                onUserChange(Number(value));
              }}
              placeholder={t("timeApproval.workerPicker.placeholder")}
              searchPlaceholder={t("common.combobox.searchPlaceholder")}
              emptyText={t("common.combobox.empty")}
              disabled={workersQuery.isPending}
              data-testid="entry-tab-worker-picker"
            />
          ) : (
            // Офис без подчинённых: субъект — сам, вместо селектора строка
            // идентичности (параллель экрану смены пароля, 73 §2.2/§2.3).
            <div
              className="flex h-9 items-center rounded-[var(--radius-button)] border border-[var(--rule-line)] bg-[var(--bg-panel-0)] px-3 text-sm font-medium text-[var(--fg-1)]"
              data-testid="entry-tab-self"
            >
              {currentUserName}
            </div>
          )}
        </div>
        <div className="flex flex-col gap-1.5 sm:w-48">
          <span className="font-condensed text-[10px] uppercase tracking-[0.22em] text-[var(--fg-3)]">
            {t("timeApproval.entryTab.dateLabel")}
          </span>
          <DateField
            value={effectiveDate ?? ""}
            onChange={(next) => {
              if (next && ISO_RE.test(next)) onDateChange(next);
            }}
            label={t("timeApproval.entryTab.dateLabel")}
            required
            data-testid="entry-tab-date"
          />
        </div>
      </Surface>

      {!hasValidSelection && (
        <Surface className="p-4 text-sm text-[var(--fg-3)]" data-testid="entry-tab-empty">
          {t("timeApproval.entryTab.empty")}
        </Surface>
      )}

      <EntryDock
        subject={subject}
        date={effectiveDate ?? ""}
        weekStart={
          effectiveDate && ISO_RE.test(effectiveDate) ? isoMonday(parseISO(effectiveDate)) : ""
        }
        unitId={null}
        surface="desktop-dock"
        onClose={() => setDismissedKey(selectionKey)}
      />
    </div>
  );
}
